import React, { useContext, useState } from 'react'
import { ButtonType, Card, CustomButton, Empty } from '../../components'
import { WemoloTypography } from '../../components/Wemolo'
import { LabeledToggleRow } from './components/LabelToggleRow'
import { LotContext } from '../Lot/LotContext'
import { OccupancySettings } from './OccupancySettings'
import { useFormSaveStore } from '../../utils'
import { OccupancyConfigState, useOccupancyConfig } from './hooks/useOccupancyConfig'
import styles from './OccupancyView.module.css'

export interface OccupancySettingsErrors {
    reservedOverCapacity: boolean
    lpThresholdOutOfRange: boolean
    parkingPermitTimeoutInvalid: boolean
    standardTimeoutInvalid: boolean
    lprOutofRange: boolean
}
export const OccupancyView = () => {
    const hasChanges = useFormSaveStore((state) => state.hasChanges)
    const lotContext = useContext(LotContext)
    const [errors, setErrors] = useState<OccupancySettingsErrors>({
        reservedOverCapacity: false,
        lpThresholdOutOfRange: false,
        parkingPermitTimeoutInvalid: false,
        standardTimeoutInvalid: false,
        lprOutofRange: false
    })
    const { errorsFetchingOccupancy } = useOccupancyConfig(lotContext.selectedLot?.id)
    const lotCapacity =
        lotContext.selectedLot?.size === null || lotContext.selectedLot?.size === undefined
            ? false
            : lotContext.selectedLot.size
    const handleInputChange = (key: keyof OccupancyConfigState, value: string | number | undefined) => {
        const newValue = value === '' ? '' : Number(value)
        setErrors((prevErrors) => {
            const newErrors = { ...prevErrors }
            if (key === 'lpness') {
                newErrors.lpThresholdOutOfRange =
                    typeof newValue === 'number' ? !(newValue >= 0 && newValue <= 1) : true
            }

            if (key === 'lot_capacity_wl') {
                newErrors.reservedOverCapacity = typeof newValue === 'number' ? newValue >= (lotCapacity || 0) : true
            }

            if (key === 'timeout_wl') {
                newErrors.parkingPermitTimeoutInvalid = typeof newValue === 'number' ? newValue < 0 : true
            }

            if (key === 'timeout') {
                newErrors.standardTimeoutInvalid = typeof newValue === 'number' ? newValue < 0 : true
            }

            if (key === 'lpr') {
                newErrors.lprOutofRange = typeof newValue === 'number' ? !(newValue >= 0 && newValue <= 1) : true
            }
            return newErrors
        })

        lotContext.setOccupancyConfig((prev) => {
            if (!prev) return prev
            return {
                ...prev,
                [key]: newValue
            }
        })
    }

    if (!lotContext.selectedLot) {
        return <Empty title="Select a lot first!" />
    }
    const handleSubmit = () => {
        const hasValidationErrors = Object.values(errors).some(Boolean)
        if (hasValidationErrors) {
            alert('Please correct the errors in the form before submitting.')
            return
        }
        lotContext.onHandleSubmit()
    }
    return (
        <div>
            <Card className={styles.card}>
                <div className={styles.header}>
                    <WemoloTypography variant="title-medium" tag="div">
                        Occupancy Calculation Configuration
                    </WemoloTypography>
                    <div className={styles.actions}>
                        <CustomButton
                            type={ButtonType.SAVE}
                            onClick={handleSubmit}
                            disabled={lotContext.saving || !hasChanges}
                            isLoading={lotContext.saving}
                        >
                            Save Configuration
                        </CustomButton>
                    </div>
                </div>

                <LabeledToggleRow
                    label="Enable Occupancy Calculation"
                    checked={lotContext.occupancyConfig?.enabled ?? false}
                    onToggle={(val) => {
                        if (!lotCapacity) return alert('Lot size is empty, contact CS to set in salesforce')
                        lotContext.setOccupancyConfig((prev) => (prev ? { ...prev, enabled: val } : prev))
                    }}
                />
                {lotContext.occupancyConfig?.enabled ? (
                    <OccupancySettings errors={errors} handleInputChange={handleInputChange} />
                ) : (
                    <Card>
                        <WemoloTypography variant="body-medium" tag="div" className={styles.disabledNotice}>
                            Occupancy calculation is currently disabled. Enable to configure calculations parameters.
                        </WemoloTypography>
                        {errorsFetchingOccupancy && errorsFetchingOccupancy.length > 0 && (
                            <WemoloTypography variant="body-small" tag="span" className={styles.errorMessage}>
                                {errorsFetchingOccupancy[0].message}
                            </WemoloTypography>
                        )}
                    </Card>
                )}
            </Card>
        </div>
    )
}
